import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { axiosInstance } from '../../shared/api/axiosInstance';
import { CompanyLogoPayload } from '../../shared/types/logoApiTypes';
import { UserProfileUpdateResponse, ValidationError } from '../../shared/types/authTypes';

export const updateCompanyLogo = createAsyncThunk<
    UserProfileUpdateResponse,
    CompanyLogoPayload,
    { rejectValue: { [key: string]: string } }
>('logo/updateCompanyLogo', async ({ userId, logoFile }, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append('file', logoFile);

    try {
        const response = await axiosInstance.post<UserProfileUpdateResponse>(
            `/logos/${userId}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            },
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to upload logo' });
    }
});

export const deleteCompanyLogo = createAsyncThunk<
    UserProfileUpdateResponse,
    { userId: string },
    { rejectValue: { [key: string]: string } }
>('logo/deleteCompanyLogo', async ({ userId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete<UserProfileUpdateResponse>(`/logos/${userId}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: 'Failed to delete logo' });
    }
});

export interface LogoState {
    isLoading: boolean;
    error: string | null;
    logoUrl: string | null;
}

export const initialState: LogoState = {
    isLoading: false,
    error: null,
    logoUrl: null,
};

const logoSlice = createSlice({
    name: 'logo',
    initialState,
    reducers: {
        setLogoUrl: (state, action: PayloadAction<string | null>) => {
            state.logoUrl = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateCompanyLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateCompanyLogo.fulfilled, (state, action) => {
                state.isLoading = false;
                state.logoUrl = action.payload.data.attributes?.logo_url ?? null;
            })
            .addCase(updateCompanyLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.general || 'Logo upload failed';
            })
            .addCase(deleteCompanyLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteCompanyLogo.fulfilled, (state) => {
                state.isLoading = false;
                state.logoUrl = null;
            })
            .addCase(deleteCompanyLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.general || 'Logo deletion failed';
            });
    },
});

export const { setLogoUrl } = logoSlice.actions;
export default logoSlice.reducer;
